import { zodResolver } from "@hookform/resolvers/zod";
import { passwordSchema } from "@shelflist/shared";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Field, fieldA11y } from "@/components/field";
import { FormError } from "@/components/form-error";
import { InvalidLink } from "@/components/invalid-link";
import { PublicLayout } from "@/components/public-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, call } from "@/lib/api";
import { applyFieldErrors, useErrorMessage } from "@/lib/errors";
import { resetLinkQuery } from "@/lib/queries";
import { useAfterSignIn } from "@/lib/session";

export const Route = createFileRoute("/reset/$token")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(resetLinkQuery(params.token)),
  component: ResetPage,
});

const formSchema = z
  .object({ password: passwordSchema, confirmPassword: z.string() })
  .refine((values) => values.password === values.confirmPassword, {
    path: ["confirmPassword"],
    error: "passwords_mismatch",
  });
type FormValues = z.input<typeof formSchema>;

function ResetPage() {
  const { t } = useTranslation(["auth", "common"]);
  const { token } = Route.useParams();
  const { data: link } = useSuspenseQuery(resetLinkQuery(token));
  const errorMessage = useErrorMessage();
  const afterSignIn = useAfterSignIn();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });
  const reset = useMutation({
    mutationFn: ({ password }: FormValues) =>
      call(api["password-reset"][":token"].$post({ param: { token }, json: { password } })),
    meta: { handlesErrors: true },
    onSuccess: () => afterSignIn("/"),
    onError: (error) => {
      applyFieldErrors(error, form.setError, ["password"]);
    },
  });
  const { errors } = form.formState;

  if (!link) {
    return (
      <InvalidLink title={t("reset.invalidTitle")} description={t("reset.invalidDescription")} />
    );
  }
  return (
    <PublicLayout
      title={t("reset.title")}
      description={t("reset.description", { name: link.name })}
    >
      <form
        noValidate
        className="grid gap-4"
        onSubmit={form.handleSubmit((values) => reset.mutate(values))}
      >
        <FormError message={reset.error ? errorMessage(reset.error) : null} />
        {/* Hidden username helps password managers store the new password for the right account. */}
        <input type="email" autoComplete="username" value={link.email} readOnly hidden />
        <Field
          id="password"
          label={t("common:fields.newPassword")}
          error={errors.password?.message}
          hint={t("common:passwordHint")}
        >
          <Input
            {...fieldA11y("password", errors.password?.message)}
            type="password"
            autoComplete="new-password"
            autoFocus
            {...form.register("password")}
          />
        </Field>
        <Field
          id="confirmPassword"
          label={t("common:fields.confirmPassword")}
          error={errors.confirmPassword?.message}
        >
          <Input
            {...fieldA11y("confirmPassword", errors.confirmPassword?.message)}
            type="password"
            autoComplete="new-password"
            {...form.register("confirmPassword")}
          />
        </Field>
        <Button type="submit" disabled={reset.isPending}>
          {t("reset.submit")}
        </Button>
      </form>
    </PublicLayout>
  );
}
